import type { FirebaseAnnouncementResponse, FirebaseDirectAnnouncement, FirebaseRequest } from '@app/constants/ApiTypes/firebaseEntities'

import { createSelector } from '@app/utils/createSelector'
import { filterObject } from '@app/utils/filterObject'
import moment from '@app/utils/moment'

import { createReduxSlice } from '@app/store/redux_slice'
import { createThunk } from '@app/store/thunk'
import type { AnnouncementResponseOffer, DirectAnnouncementOffer, FirebaseBatch, RequestOffer } from '@app/store/types/firebase'

export type FirebaseOffersState = {
  requests: { [key: string]: RequestOffer }
  announcement_responses: { [key: string]: AnnouncementResponseOffer }
  direct_announcements: { [key: string]: DirectAnnouncementOffer }
}

const firebaseOffersSlice = createReduxSlice<FirebaseOffersState>('firebase_offers')

export const firebaseOffersSelector = createSelector(
  [firebaseOffersSlice.selector],
  offers => offers ?? { requests: {}, announcement_responses: {}, direct_announcements: {} }
)

export const setFirebaseOffers = (payload: FirebaseBatch) =>
  createThunk((dispatch, getState) => {
    const current = firebaseOffersSelector(getState())
    const next = mergeOffers(current, payload)
    if (next !== current) dispatch(firebaseOffersSlice.set(next))
  })

function mergeOffers(state: FirebaseOffersState, payload: FirebaseBatch): FirebaseOffersState {
  const reqs = Object.fromEntries(Object.values(payload.requests.models).map(e => [e.id, toRequestOffer(e)] as const))
  const ars = Object.fromEntries(Object.values(payload.announcement_responses.models).map(e => [e.id, toAnnouncementResponseOffer(e)] as const))
  const das = Object.fromEntries(Object.values(payload.direct_announcements.models).map(e => [e.id, toDirectAnnouncementOffer(e)] as const))

  const reqRemoved = payload.requests.removed
  const arRemoved = payload.announcement_responses.removed
  const daRemoved = payload.direct_announcements.removed

  if (!reqRemoved.size && !Object.keys(reqs).length && !arRemoved.size && !Object.keys(ars).length && !daRemoved.size && !Object.keys(das).length) {
    return state
  }

  const newState: FirebaseOffersState = {
    requests: Object.keys(reqs).length ? { ...state.requests, ...reqs } : state.requests,
    announcement_responses: Object.keys(ars).length ? { ...state.announcement_responses, ...ars } : state.announcement_responses,
    direct_announcements: Object.keys(das).length ? { ...state.direct_announcements, ...das } : state.direct_announcements,
  }

  if (reqRemoved.size) {
    newState.requests = filterObject(newState.requests, (_k, v) => !reqRemoved.has(v.id))
  }
  if (arRemoved.size) {
    newState.announcement_responses = filterObject(newState.announcement_responses, (_k, v) => !arRemoved.has(v.id))
  }
  if (daRemoved.size) {
    newState.direct_announcements = filterObject(newState.direct_announcements, (_k, v) => !daRemoved.has(v.id))
  }

  return newState
}

const toRequestOffer = (e: FirebaseRequest): RequestOffer => ({
  type: e.type,
  id: e.id,
  date: moment(e.attributes.date + ' ' + e.attributes.start_time, 'YYYY-MM-DD HH:mm').toDate(),
  attributes: e.attributes,
})

const toAnnouncementResponseOffer = (e: FirebaseAnnouncementResponse): AnnouncementResponseOffer => ({
  type: e.type,
  id: e.id,
  date: moment(e.attributes.announcement.date + ' ' + e.attributes.announcement.start_time, 'YYYY-MM-DD HH:mm').toDate(),
  attributes: e.attributes,
})

const toDirectAnnouncementOffer = (e: FirebaseDirectAnnouncement): DirectAnnouncementOffer => ({
  type: e.type,
  id: e.id,
  date: moment(e.attributes.announcement.date + ' ' + e.attributes.announcement.start_time, 'YYYY-MM-DD HH:mm').toDate(),
  attributes: e.attributes,
})
